const MARGIN = 10;
const LETTER_PORTRAIT_W  = 215.9 - MARGIN * 2;
const LETTER_LANDSCAPE_W = 279.4 - MARGIN * 2;

// UUID v4 pattern — columns whose values are all UUIDs add no value to a printed report
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Column keys that look like dollar amounts
const CURRENCY_KEYS = /amt|amount|price|pipeline|won|lost|deal|revenue|value|velocity/i;

// Win rate / open share columns hold fractions; counts ending _n are never dollars (closed_deals_n, win_rate_n)
const FRACTION_KEYS = /(^|_)(win_rate|open_share|closed_deal_share)$/i;
const COUNT_KEYS = /_n$|_count$/i;

/**
 * Returns true if a column appears to contain only UUIDs (sampled from first rows).
 */
function isUuidColumn(data, key) {
  const sample = data.slice(0, 20);
  return sample.length > 0 && sample.every(row => {
    const v = row[key];
    return v == null || v === '' || UUID_RE.test(String(v));
  });
}

/**
 * Format a cell value for PDF display.
 * - Currency columns get $X,XXX.XX formatting
 * - Dates are left as-is
 * - Everything else is stringified
 */
function formatCell(value, key) {
  if (value == null || value === '') return '';
  const num = Number(value);
  if (FRACTION_KEYS.test(key) && !isNaN(num) && num >= 0 && num <= 1) return `${(num * 100).toFixed(1)}%`;
  if (COUNT_KEYS.test(key)) return String(value);
  if (CURRENCY_KEYS.test(key) && !isNaN(num) && num !== 0) {
    return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return String(value);
}

/**
 * Measure the "natural" width each column wants based on actual content.
 * Returns an array of relative weights (character counts).
 */
function measureColumns(headers, body) {
  return headers.map((h, i) => {
    let maxLen = h.length;
    const sample = body.slice(0, 50);
    for (const row of sample) {
      const len = String(row[i] ?? '').length;
      if (len > maxLen) maxLen = len;
    }
    return maxLen;
  });
}

// Columns that need extra room in PDF (emails, currency amounts)
const WIDE_KEYS = /email|amt|amount|pipeline|sumoppamt|oppamt/i;
// Columns that can be narrower (short codes, IDs)
const NARROW_KEYS = /^workshopID$|^ID$|^checkin_id$|^score$|^total_tagscore$|^quizpossiblescore$|^oppcount$/i;

/**
 * Allocate column widths proportional to content, with boosts for
 * email/currency and caps for short code columns, then scale to page.
 */
function allocateWidths(headers, body, pageWidth, fontSize, keys) {
  const weights = measureColumns(headers, body);
  const mmPerChar = fontSize * 0.22;

  const MIN_COL = Math.max(8, fontSize * 1.5);
  const MAX_COL = pageWidth * 0.30;
  const raw = weights.map((w, i) => {
    let mm = w * mmPerChar;
    const key = keys?.[i] || '';
    if (WIDE_KEYS.test(key)) mm = Math.max(mm, 28);   // minimum 28mm for email/currency
    if (NARROW_KEYS.test(key)) mm = Math.min(mm, 18);  // cap short columns at 18mm
    return Math.max(MIN_COL, Math.min(MAX_COL, mm));
  });

  const total = raw.reduce((s, w) => s + w, 0);
  const scale = pageWidth / total;
  return raw.map(w => w * scale);
}

/**
 * Pick orientation and font size so the table fits on US Letter.
 */
function pickLayout(numCols) {
  if (numCols <= 6)  return { orientation: 'portrait',  fontSize: 8 };
  if (numCols <= 10) return { orientation: 'landscape', fontSize: 7 };
  if (numCols <= 14) return { orientation: 'landscape', fontSize: 6 };
  return { orientation: 'landscape', fontSize: 5.5 };
}

/**
 * Extract visible column definitions from a material-react-table instance.
 * Excludes internal MRT columns (selection checkbox, row actions, expand, etc.).
 */
export function getVisibleColumns(table) {
  const MRT_PREFIXES = ['mrt-row'];
  return table.getVisibleLeafColumns()
    .filter(col => !MRT_PREFIXES.some(p => col.id.startsWith(p)))
    .map(col => ({
      header: typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id,
      key: col.id,
    }));
}

/**
 * Export an array of flat objects to a well-fitted PDF table on US Letter.
 *
 * - Automatically chooses portrait vs landscape based on column count.
 * - Allocates column widths proportionally to content length.
 * - Formats currency columns with $ and commas.
 * - Drops UUID-only columns and workshopTitle automatically.
 */
export async function exportToPdf(data, { title = 'Export', columns } = {}) {
  if (!data || data.length === 0) return;
  const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);

  const allHeaders = columns
    ? columns.map(c => (typeof c === 'string' ? c : c.header))
    : Object.keys(data[0]);
  const allKeys = columns
    ? columns.map(c => (typeof c === 'string' ? c : c.key))
    : Object.keys(data[0]);

  // Drop columns not useful on a printed report
  const PDF_EXCLUDE_KEYS = new Set(['workshopTitle', 'workshoptitle', 'workshop']);
  const keep = allKeys.map((k, i) => ({ header: allHeaders[i], key: k }))
    .filter(({ key }) => !isUuidColumn(data, key) && !PDF_EXCLUDE_KEYS.has(key));
  const headers = keep.map(c => c.header);
  const keys = keep.map(c => c.key);

  const body = data.map(row => keys.map(k => formatCell(row[k], k)));
  const { orientation, fontSize } = pickLayout(headers.length);
  const pageWidth = orientation === 'landscape' ? LETTER_LANDSCAPE_W : LETTER_PORTRAIT_W;

  // Proportional column widths that sum exactly to page width
  const widths = allocateWidths(headers, body, pageWidth, fontSize, keys);
  const columnStyles = {};
  for (let i = 0; i < headers.length; i++) {
    columnStyles[i] = { cellWidth: widths[i] };
    // Right-align currency columns
    if (CURRENCY_KEYS.test(keys[i])) {
      columnStyles[i].halign = 'right';
    }
  }

  const doc = new jsPDF({ orientation, format: 'letter' });
  doc.setFontSize(11);
  doc.text(title, MARGIN, 14);

  autoTable(doc, {
    head: [headers],
    body,
    startY: 20,
    margin: { left: MARGIN, right: MARGIN },
    styles: {
      fontSize,
      cellPadding: 1,
      overflow: 'linebreak',
    },
    columnStyles,
    headStyles: { fillColor: [218, 41, 28], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 248, 248] },
  });

  doc.save(`${title.replace(/[^a-zA-Z0-9]+/g, '_').toLowerCase()}.pdf`);
}
